import React, { useState } from "react";
import {
  Box,
  Text,
  Radio,
  RadioGroup,
  Flex,
  Input,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { FileIcon } from "@root/assets/icons/file-icon";
import { FILE_STATUS, ACTION } from "@root/constants";
import { useTranslation } from "react-i18next";
import {
  FileCardContainer,
  FileNameContainer,
  StatusContainer,
} from "@root/pages/material-camparison/style";
import { formatDateToISO } from "@root/utils/date-helper";
import { formatFileSizeSmart } from "@root/utils/file-size-helper";
import TrashIcon from "@root/assets/icons/trashicon";
import EditIcon from "@root/assets/icons/pencil";
import { ModalDelete } from "@root/components/modal-delete";
import { useProjectsContext } from "@root/context/projects-context";
import { CommonTooltip } from "@root/components/common/tooltip/CommonTooltip";

interface FilesListProps {
  files?: any[];
  onFileSelect: (file: any) => void;
  selectedFile: any;
  fileType: "contract" | "drawing";
  updateDocument?: (id: number, payload: any) => Promise<void>;
  deleteDocument?: (id: number) => Promise<void>;
  updateDocumentLoading?: boolean;
  deleteDocumentLoading?: boolean;
  onRefresh?: () => void;
}

const FilesList: React.FC<FilesListProps> = ({
  files = [],
  onFileSelect,
  selectedFile,
  fileType,
  onRefresh,
}) => {
  const { t } = useTranslation("common");

  // Edit/Delete state management
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);
  const handleFileSelection = (fileId: string) => {
    const selectedFileObj = files.find((file) => file.id.toString() === fileId);
    if (selectedFileObj) {
      onFileSelect(selectedFileObj);
    }
  };

  const {
    deleteDocument,
    deleteDocumentLoading,
    updateDocument,
    updateDocumentLoading,
  } = useProjectsContext();

  const STATUS_STYLE = {
    COMPLETED: {
      bg: "#ECFDF3",
      color: "#3AB65A",
    },
    PROCESSING: {
      bg: "#FFFAEB",
      color: "#B54708",
    },
  };

  const handleCardClick = (fileObj: any) => {
    onFileSelect(fileObj);
  };

  const handleActions = (action: ACTION, file: any) => {
    if (action === ACTION.DELETE) {
      setSelectedDeleteId(file?.id ?? null);
      onOpen();
    } else if (action === ACTION.EDIT) {
      setEditingRowId(file?.id);
      setEditingValue(file?.fileName_key || "");
    }
  };

  const handleSaveEdit = async () => {
    if (!editingRowId || !editingValue.trim() || !updateDocument) {
      return;
    }

    try {
      await updateDocument(editingRowId, { fileName_key: editingValue.trim() });
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Failed to update document:", error);
    } finally {
      setEditingRowId(null);
      setEditingValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditingValue("");
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteId || !deleteDocument) {
      onClose();
      return;
    }
    try {
      await deleteDocument(selectedDeleteId);
      if (onRefresh) {
        onRefresh();
      }
    } catch (e) {
      console.error("Failed to delete document:", e);
    } finally {
      setSelectedDeleteId(null);
      onClose();
    }
  };

  // Common file preview card (to avoid duplication)
  const renderFileCard = (fileObj: any) => {
    const isSelected = selectedFile?.id === fileObj?.id;

    const renderStatusBadge = (status: number) => {
      const statusStyle = STATUS_STYLE.COMPLETED; // Might need to make dynamic later
      return (
        <Flex
          as={"span"}
          alignItems="center"
          gap={2}
          borderRadius="36px"
          height="24px"
          px={3}
          bg={statusStyle.bg}
          width="fit-content"
        >
          <Flex
            width="8px"
            height="8px"
            borderRadius="50%"
            bg={statusStyle.color}
          />
          <Text fontSize="12px" fontWeight={500} color={statusStyle.color}>
            {t("fileTable.uploadedStatus")}
          </Text>
        </Flex>
      );
    };

    const isEditing = editingRowId === fileObj?.id;

    return (
      <FileCardContainer
        key={fileObj?.fileName_key}
        bg={isSelected ? "#F7FBFE" : "white"}
        borderColor="#E2E8F0"
        onClick={() => handleCardClick(fileObj)}
        cursor="pointer"
      >
        <Box mr={4} p={2}>
          <Radio
            value={fileObj?.id?.toString()}
            size="lg"
            isChecked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              handleCardClick(fileObj);
            }}
            sx={{
              "& .chakra-radio__control": {
                width: "22px",
                height: "22px",
                border: "2px solid",
                borderColor: isSelected ? "#40599B" : "#5A6FA8",
                _checked: {
                  borderColor: "#40599B",
                  bg: "#40599B",
                  _before: {
                    content: '""',
                    display: "block",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    bg: "white",
                  },
                },
                _hover: {
                  borderColor: "#2D3F6B",
                  bg: "#F0F4F8",
                },
              },
              "& .chakra-radio__inner": {
                width: "14px",
                height: "14px",
              },
            }}
          />
        </Box>
        <FileIcon
          marginRight={3}
          width={"32px"}
          height={"32px"}
          alignSelf="center"
        />
        <Box w="100%">
          {/* Top row: filename + status badge */}
          <FileNameContainer gap={"20px"}>
            <Box flex="1">
              <Flex gap={"15px"}>
                {isEditing ? (
                  <Flex alignItems="center" gap="8px" width="100%">
                    <Input
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      size="sm"
                      variant="outline"
                      borderColor="#40599B"
                      _focus={{
                        borderColor: "#40599B",
                        boxShadow: "0 0 0 1px #40599B",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSaveEdit();
                        } else if (e.key === "Escape") {
                          handleCancelEdit();
                        }
                      }}
                      autoFocus
                      flex="1"
                      placeholder={t("fileTable.enterFileName")}
                    />
                    <Flex
                      cursor={updateDocumentLoading ? "not-allowed" : "pointer"}
                      onClick={
                        updateDocumentLoading ? undefined : handleSaveEdit
                      }
                      opacity={updateDocumentLoading ? 0.6 : 1}
                      p="4px"
                      borderRadius="4px"
                      _hover={{
                        bg: updateDocumentLoading ? "transparent" : "gray.100",
                      }}
                      title={t("fileTable.saveChanges")}
                    >
                      {updateDocumentLoading ? (
                        <Spinner size="sm" color="#40599B" />
                      ) : (
                        <CheckIcon boxSize={4} color="#40599B" />
                      )}
                    </Flex>
                    <Flex
                      cursor="pointer"
                      onClick={handleCancelEdit}
                      p="4px"
                      borderRadius="4px"
                      _hover={{ bg: "gray.100" }}
                      title={t("fileTable.cancelEdit")}
                    >
                      <CloseIcon boxSize={3} color="#40599B" />
                    </Flex>
                  </Flex>
                ) : (
                  <Text fontWeight="medium" color={"#2F2F34"} noOfLines={1}>
                    {fileObj?.fileName_key}
                  </Text>
                )}
                {renderStatusBadge(fileObj?.status)}
              </Flex>
              <Flex
                marginTop={"5px"}
                alignItems={"center"}
                gap={"15px"}
                lineHeight={1.2}
              >
                <Text fontSize={"14px"} color="#5C6670">
                  {formatFileSizeSmart(Number(fileObj?.fileSize) || 0)}
                </Text>
                <Text fontSize={"14px"} color="#5C6670">
                  <Box as="span" marginRight={"6px"}>
                    {t("dashboard.uploaded")}
                  </Box>
                  <Box as="span">{formatDateToISO(fileObj?.createdAt)}</Box>
                </Text>
              </Flex>
            </Box>
          </FileNameContainer>
        </Box>
        {isSelected && (
          <>
            {!isEditing && (
              <CommonTooltip label={t("tooltip.editFileName")}>
                <Box mx={2}>
                  <EditIcon
                    width={24}
                    height={24}
                    fill="#6D6D70"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent triggering card click
                      handleActions(ACTION.EDIT, fileObj);
                    }}
                  />
                </Box>
              </CommonTooltip>
            )}
            <CommonTooltip label={t("tooltip.deleteFile")}>
              <Box>
                <TrashIcon
                  width={24}
                  height={24}
                  fill="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActions(ACTION.DELETE, fileObj);
                  }}
                />
              </Box>
            </CommonTooltip>
          </>
        )}
      </FileCardContainer>
    );
  };

  return (
    <>
      <RadioGroup
        value={selectedFile?.id?.toString() || ""}
        onChange={handleFileSelection}
      >
        {files?.map((fileObj: any) => renderFileCard(fileObj))}
      </RadioGroup>

      <ModalDelete
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        isLoading={deleteDocumentLoading}
        title={t("chat.areYouSureDelete")}
        description={t("chat.cannotBeUndone")}
        deleteButtonTitle={t("chat.deleteBtn", { defaultValue: "Delete" })}
        cancelButtonTitle={t("chat.cancelBtn", { defaultValue: "Cancel" })}
      />
    </>
  );
};

export default FilesList;
